import React, { Dispatch, SetStateAction, useContext, useMemo } from 'react';
import { UserContext } from '../common/auth/UserContext';
import { StepsIndexConstant } from '../common/components/Stepper';
import { useStepper } from '../common/components/StepperContext';
import { CONSTANTS_ID, INACTIVE_FALSE_FILTER } from '../common/constants/constants';
import FormModalInfoAlert from '../common/form/FormModalInfoAlert';
import I18n, { useI18n } from '../common/i18n/I18n';
import { V } from '../common/Layout';
import { RESOURCE_TYPE } from '../resource/common/resourceConstants';
import { toInputForSaveResource } from '../resource/resourceUtils';
import {
    getInquiryPersonalisedMessageKey,
    ORGANIZATION_TYPE,
    OrganizationInviteFormData,
    organizationValidationRules,
    PEOPLE_TYPE,
    PeopleType,
    PersonInviteFormData,
    personValidationRules,
    useInquiryInviteeData
} from '../shortlists/form/inquiry/inquiry';
import InquiryForm from '../shortlists/form/inquiry/InquiryForm';
import {
    defineCommonInquiryProductionData,
    productionOtherTitlesToOptions,
    shortlistInviteeCrewFilter,
    shortlistInviteeOrgFilter
} from '../shortlists/shortlistLineItem';

type HiredPersonFormData = {
    person: PeopleType;
};

export default ({
    resType,
    setIsExpandedModal,
    expandedContainer,
    showCandidateListOnly = false,
    formAlertTitleToken,
    formAlertDescriptionToken,
    formAlertDescriptionList
}: {
    resType: string;
    setIsExpandedModal: Dispatch<SetStateAction<boolean>>;
    expandedContainer: boolean;
    showCandidateListOnly?: boolean;
    formAlertTitleToken?: string;
    formAlertDescriptionToken?: string;
    formAlertDescriptionList?: string[];
}) => {
    const { organizationAccountDetails } = useContext(UserContext);
    const { getStepData } = useStepper();
    const isPeopleType = [RESOURCE_TYPE.crew, RESOURCE_TYPE.cast, RESOURCE_TYPE.corporate].includes(resType);
    const { inviteeSearchData, isSearchLoading } = useInquiryInviteeData(isPeopleType, []);

    let resData, prodData;
    let descriptionToken = 'stepper.hiring.request.avails.info.crew.description';
    let title = 'stepper.hiring.request.avails.crew.info.title';
    if (resType === RESOURCE_TYPE.supplier) {
        const resDataFormValue = getStepData(StepsIndexConstant.one);
        resData = toInputForSaveResource(resDataFormValue.identity?.id || CONSTANTS_ID.createId, resDataFormValue);
        prodData = getStepData(StepsIndexConstant.two);
        descriptionToken = 'stepper.hiring.request.avails.info.supplier.description';
        title = 'stepper.hiring.request.avails.supplier.info.title';
    } else {
        resData = getStepData(StepsIndexConstant.zero);
        prodData = getStepData(StepsIndexConstant.one);
    }

    const productionOptions = useMemo(
        () => productionOtherTitlesToOptions(prodData?.otherTitles),
        [prodData?.otherTitles]
    );
    const personalizedMessage = useI18n(getInquiryPersonalisedMessageKey(isPeopleType), {
        resourceCategory: resData?.resourceCategory
    });

    const commonInquiryProductionData = useMemo(() => {
        if (!showCandidateListOnly && resData && prodData) {
            const prodNameAsString = prodData.name?.label || prodData.name;
            return defineCommonInquiryProductionData(
                resData,
                { ...prodData, name: prodNameAsString },
                productionOptions,
                personalizedMessage,
                organizationAccountDetails?.organizationName
            );
        }
    }, [resData, prodData]);

    let data;
    if (isPeopleType) {
        if (showCandidateListOnly) {
            data = { person: undefined } as HiredPersonFormData;
        } else {
            data = {
                role: resData?.role,
                people: [],
                ...commonInquiryProductionData
            } as PersonInviteFormData;
        }
    } else {
        data = {
            activities: [],
            notifyContacts: [],
            senderContacts: [],
            organizations: [],
            ...commonInquiryProductionData
        } as OrganizationInviteFormData;
    }

    const getFilters = () => {
        if (showCandidateListOnly) return undefined;
        if (isPeopleType) return shortlistInviteeCrewFilter(resData?.locations, resData?.role);
        const orgFilter = shortlistInviteeOrgFilter(resData?.locations, resData?.resourceCategory);
        if (orgFilter) return orgFilter;
        return INACTIVE_FALSE_FILTER;
    };
    const filters = getFilters();

    return (
        <V fill>
            <FormModalInfoAlert
                title={formAlertTitleToken ?? title}
                descriptionToken={formAlertDescriptionToken ?? descriptionToken}
            >
                {formAlertDescriptionToken ? <I18n token={formAlertDescriptionToken} /> : <></>}
                {formAlertDescriptionList && formAlertDescriptionList.length > 0 ? (
                    <ul>
                        {formAlertDescriptionList.map((item, idx) => (
                            <li key={idx}>
                                <I18n token={item} />
                            </li>
                        ))}
                    </ul>
                ) : (
                    <></>
                )}
            </FormModalInfoAlert>

            <InquiryForm
                type={isPeopleType ? PEOPLE_TYPE : ORGANIZATION_TYPE}
                validationRules={
                    isPeopleType ? personValidationRules(showCandidateListOnly) : organizationValidationRules
                }
                entityDetails={inviteeSearchData?.search?.hits}
                expandMarketplace={() => setIsExpandedModal((prev) => !prev)}
                isEntityLoading={isSearchLoading}
                data={{ ...data }}
                isRequestAvailsForUser
                filters={filters}
                isInStepper
                expandedContainer={expandedContainer}
                showCandidateListOnly={showCandidateListOnly}
            />
        </V>
    );
};
